import type { LanguageCode } from "../../utils/translations";

export interface UseCaseSection {
  heading: string;
  /** Optional paragraph shown under the heading. */
  body?: string;
  /** Optional bullet list shown under the heading. */
  items?: string[];
}

export interface UseCaseContent {
  title: string;
  intro: string;
  sections: UseCaseSection[];
  /** Optional call-to-action button label. */
  cta?: string;
}

export type UseCaseKey =
  | "instagram"
  | "facebook"
  | "socialAutomation"
  | "smsMarketing"
  | "emailMarketing"
  | "bulkEmail"
  | "multiAccount";

const EN: Record<UseCaseKey, UseCaseContent> = {
  instagram: {
      title: "Manage Multiple Instagram Accounts Easily",
      intro:
        "Managing multiple Instagram accounts can be challenging for businesses, agencies, and marketers. SocialFlow provides a powerful platform to handle multiple accounts efficiently without switching devices or risking account bans.",
      sections: [
        {
          heading: "Why Manage Multiple Instagram Accounts?",
          body: "Businesses often need multiple Instagram profiles for branding, marketing campaigns, and audience segmentation. Managing them manually can waste time and reduce productivity.",
        },
        {
          heading: "Challenges Without a Tool",
          items: [
            "Frequent logins and logouts",
            "Risk of account suspension",
            "No centralized control",
            "Time-consuming management",
          ],
        },
        {
          heading: "How SocialFlow Helps",
          items: [
            "Manage all accounts from one dashboard",
            "Automate posting and engagement",
            "Secure login handling",
            "Analytics and performance tracking",
          ],
        },
      ],
      cta: "Get Started with SocialFlow",
    },
    facebook: {
      title: "Manage Multiple Facebook Accounts",
      intro:
        "SocialFlow allows businesses and marketers to manage multiple Facebook accounts efficiently from a single dashboard.",
      sections: [
        {
          heading: "Why Multiple Accounts?",
          body: "Agencies and businesses often need multiple accounts for campaigns, clients, and audience segmentation.",
        },
        {
          heading: "How SocialFlow Helps",
          items: [
            "Centralized dashboard",
            "Automation tools",
            "Secure account handling",
          ],
        },
      ],
    },
    socialAutomation: {
      title: "Social Media Automation",
      intro:
        "Automate posting, engagement, and analytics across all platforms with SocialFlow.",
      sections: [
        {
          heading: "Benefits",
          items: ["Save time", "Increase engagement", "Scale faster"],
        },
      ],
    },
    smsMarketing: {
      title: "SMS Marketing Platform",
      intro:
        "Reach your customers instantly with bulk SMS campaigns using SocialFlow.",
      sections: [
        {
          heading: "Features",
          items: ["Bulk messaging", "Delivery tracking", "Automation"],
        },
      ],
    },
    emailMarketing: {
      title: "Email Marketing",
      intro:
        "Create and manage powerful email campaigns with automation and analytics.",
      sections: [
        {
          heading: "Why Use Email Marketing?",
          items: ["High ROI", "Direct communication", "Automation"],
        },
      ],
    },
    bulkEmail: {
      title: "Bulk Email Campaigns",
      intro:
        "Send large-scale email campaigns with ease using SocialFlow tools.",
      sections: [
        {
          heading: "Features",
          items: ["Mass sending", "Analytics", "Automation workflows"],
        },
      ],
    },
    multiAccount: {
      title: "Multi Account Manager",
      intro:
        "Manage multiple social media accounts from one unified dashboard with SocialFlow.",
      sections: [
        {
          heading: "Benefits",
          items: ["Central control", "Automation", "Security"],
        },
      ],
    },
};

const HI: Record<UseCaseKey, UseCaseContent> = {
    instagram: {
      title: "कई इंस्टाग्राम अकाउंट्स आसानी से मैनेज करें",
      intro:
        "कई इंस्टाग्राम अकाउंट्स मैनेज करना बिज़नेस, एजेंसियों और मार्केटर्स के लिए चुनौतीपूर्ण हो सकता है। SocialFlow एक शक्तिशाली प्लेटफ़ॉर्म देता है जिससे आप डिवाइस बदले बिना या अकाउंट बैन का जोखिम उठाए बिना कई अकाउंट्स कुशलता से संभाल सकते हैं।",
      sections: [
        {
          heading: "कई इंस्टाग्राम अकाउंट्स क्यों मैनेज करें?",
          body: "बिज़नेस को ब्रांडिंग, मार्केटिंग कैंपेन और ऑडियंस सेगमेंटेशन के लिए अक्सर कई इंस्टाग्राम प्रोफ़ाइल चाहिए। इन्हें मैन्युअली मैनेज करने में समय बर्बाद होता है और उत्पादकता घटती है।",
        },
        {
          heading: "बिना टूल के चुनौतियां",
          items: [
            "बार-बार लॉगिन और लॉगआउट",
            "अकाउंट सस्पेंड होने का जोखिम",
            "कोई केंद्रीय नियंत्रण नहीं",
            "समय लेने वाला प्रबंधन",
          ],
        },
        {
          heading: "SocialFlow कैसे मदद करता है",
          items: [
            "सभी अकाउंट्स एक डैशबोर्ड से मैनेज करें",
            "पोस्टिंग और एंगेजमेंट ऑटोमेट करें",
            "सुरक्षित लॉगिन हैंडलिंग",
            "एनालिटिक्स और परफ़ॉर्मेंस ट्रैकिंग",
          ],
        },
      ],
      cta: "SocialFlow के साथ शुरू करें",
    },
    facebook: {
      title: "कई फेसबुक अकाउंट्स मैनेज करें",
      intro:
        "SocialFlow बिज़नेस और मार्केटर्स को एक ही डैशबोर्ड से कई फेसबुक अकाउंट्स कुशलता से मैनेज करने देता है।",
      sections: [
        {
          heading: "कई अकाउंट्स क्यों?",
          body: "एजेंसियों और बिज़नेस को कैंपेन, क्लाइंट्स और ऑडियंस सेगमेंटेशन के लिए अक्सर कई अकाउंट्स चाहिए।",
        },
        {
          heading: "SocialFlow कैसे मदद करता है",
          items: [
            "केंद्रीकृत डैशबोर्ड",
            "ऑटोमेशन टूल्स",
            "सुरक्षित अकाउंट हैंडलिंग",
          ],
        },
      ],
    },
    socialAutomation: {
      title: "सोशल मीडिया ऑटोमेशन",
      intro:
        "SocialFlow के साथ सभी प्लेटफ़ॉर्म्स पर पोस्टिंग, एंगेजमेंट और एनालिटिक्स ऑटोमेट करें।",
      sections: [
        {
          heading: "फायदे",
          items: ["समय बचाएं", "एंगेजमेंट बढ़ाएं", "तेज़ी से स्केल करें"],
        },
      ],
    },
    smsMarketing: {
      title: "SMS मार्केटिंग प्लेटफ़ॉर्म",
      intro:
        "SocialFlow के साथ बल्क SMS कैंपेन से अपने ग्राहकों तक तुरंत पहुंचें।",
      sections: [
        {
          heading: "विशेषताएं",
          items: ["बल्क मैसेजिंग", "डिलीवरी ट्रैकिंग", "ऑटोमेशन"],
        },
      ],
    },
    emailMarketing: {
      title: "ईमेल मार्केटिंग",
      intro:
        "ऑटोमेशन और एनालिटिक्स के साथ शक्तिशाली ईमेल कैंपेन बनाएं और मैनेज करें।",
      sections: [
        {
          heading: "ईमेल मार्केटिंग क्यों इस्तेमाल करें?",
          items: ["उच्च ROI", "सीधा संवाद", "ऑटोमेशन"],
        },
      ],
    },
    bulkEmail: {
      title: "बल्क ईमेल कैंपेन",
      intro:
        "SocialFlow टूल्स के साथ बड़े पैमाने पर ईमेल कैंपेन आसानी से भेजें।",
      sections: [
        {
          heading: "विशेषताएं",
          items: ["मास सेंडिंग", "एनालिटिक्स", "ऑटोमेशन वर्कफ़्लो"],
        },
      ],
    },
    multiAccount: {
      title: "मल्टी अकाउंट मैनेजर",
      intro:
        "SocialFlow के साथ एक ही एकीकृत डैशबोर्ड से कई सोशल मीडिया अकाउंट्स मैनेज करें।",
      sections: [
        {
          heading: "फायदे",
          items: ["केंद्रीय नियंत्रण", "ऑटोमेशन", "सुरक्षा"],
        },
      ],
    },
};

const FR: Record<UseCaseKey, UseCaseContent> = {
  instagram: {
    title: "Gérez facilement plusieurs comptes Instagram",
    intro:
      "Gérer plusieurs comptes Instagram peut être un défi pour les entreprises, les agences et les marketeurs. SocialFlow offre une plateforme puissante pour gérer plusieurs comptes efficacement, sans changer d'appareil ni risquer une suspension de compte.",
    sections: [
      {
        heading: "Pourquoi gérer plusieurs comptes Instagram ?",
        body: "Les entreprises ont souvent besoin de plusieurs profils Instagram pour l'image de marque, les campagnes marketing et la segmentation de l'audience. Les gérer manuellement peut faire perdre du temps et réduire la productivité.",
      },
      {
        heading: "Les défis sans outil",
        items: [
          "Connexions et déconnexions fréquentes",
          "Risque de suspension de compte",
          "Aucun contrôle centralisé",
          "Une gestion chronophage",
        ],
      },
      {
        heading: "Comment SocialFlow aide",
        items: [
          "Gérez tous les comptes depuis un seul tableau de bord",
          "Automatisez les publications et l'engagement",
          "Gestion sécurisée des connexions",
          "Analyses et suivi des performances",
        ],
      },
    ],
    cta: "Commencer avec SocialFlow",
  },
  facebook: {
    title: "Gérez plusieurs comptes Facebook",
    intro:
      "SocialFlow permet aux entreprises et aux marketeurs de gérer efficacement plusieurs comptes Facebook depuis un seul tableau de bord.",
    sections: [
      {
        heading: "Pourquoi plusieurs comptes ?",
        body: "Les agences et les entreprises ont souvent besoin de plusieurs comptes pour les campagnes, les clients et la segmentation de l'audience.",
      },
      {
        heading: "Comment SocialFlow aide",
        items: [
          "Tableau de bord centralisé",
          "Outils d'automatisation",
          "Gestion sécurisée des comptes",
        ],
      },
    ],
  },
  socialAutomation: {
    title: "Automatisation des réseaux sociaux",
    intro:
      "Automatisez les publications, l'engagement et les analyses sur toutes les plateformes avec SocialFlow.",
    sections: [
      {
        heading: "Avantages",
        items: ["Gagnez du temps", "Augmentez l'engagement", "Évoluez plus vite"],
      },
    ],
  },
  smsMarketing: {
    title: "Plateforme de marketing SMS",
    intro:
      "Touchez vos clients instantanément avec des campagnes SMS en masse grâce à SocialFlow.",
    sections: [
      {
        heading: "Fonctionnalités",
        items: ["Envoi en masse", "Suivi de livraison", "Automatisation"],
      },
    ],
  },
  emailMarketing: {
    title: "Marketing par e-mail",
    intro:
      "Créez et gérez des campagnes e-mail performantes avec automatisation et analyses.",
    sections: [
      {
        heading: "Pourquoi utiliser le marketing par e-mail ?",
        items: ["ROI élevé", "Communication directe", "Automatisation"],
      },
    ],
  },
  bulkEmail: {
    title: "Campagnes d'e-mails en masse",
    intro:
      "Envoyez facilement des campagnes e-mail à grande échelle grâce aux outils SocialFlow.",
    sections: [
      {
        heading: "Fonctionnalités",
        items: ["Envoi massif", "Analyses", "Flux d'automatisation"],
      },
    ],
  },
  multiAccount: {
    title: "Gestionnaire multi-comptes",
    intro:
      "Gérez plusieurs comptes de réseaux sociaux depuis un tableau de bord unifié avec SocialFlow.",
    sections: [
      {
        heading: "Avantages",
        items: ["Contrôle central", "Automatisation", "Sécurité"],
      },
    ],
  },
};

export const USECASE_COPY: Record<
  LanguageCode,
  Record<UseCaseKey, UseCaseContent>
> = {
  en: EN,
  hi: HI,
  fr: FR,
};

export const getUseCase = (
  lang: LanguageCode,
  key: UseCaseKey
): UseCaseContent => USECASE_COPY[lang][key];
